import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import Close from 'dibs-icons/exports/legacy/Close.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { QuickViewCreators } from 'dibs-search-product-tile/exports/QuickViewCreators.tsx';
import { SeoButton } from 'dibs-seo/exports/SeoButton.tsx';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import classNames from 'classnames';

import styles from './SbSharedCreatorDesignsQuickView.scss';

import { type SbSharedCreatorDesignsQuickView_quickViewDisplay$key } from './__generated__/SbSharedCreatorDesignsQuickView_quickViewDisplay.graphql.ts';

type Props = {
    quickViewDisplay: SbSharedCreatorDesignsQuickView_quickViewDisplay$key | null | undefined;
    onClose: () => void;
    isVisible: boolean;
    isMobile?: boolean;
};

export const SbSharedCreatorDesignsQuickView: FC<Props> = ({
    quickViewDisplay: quickViewDisplayRef,
    onClose,
    isVisible,
    isMobile,
}) => {
    const quickViewDisplay = useFragment(
        graphql`
            fragment SbSharedCreatorDesignsQuickView_quickViewDisplay on QuickViewDisplayType {
                title
                linkData {
                    ...SeoLink_linkData
                    ...SeoButton_linkData
                }
                paragraphs {
                    key
                    text
                }
                paragraphAttributeNames {
                    style
                    material
                }
                ...QuickViewCreators_quickViewDisplay
            }
        `,
        quickViewDisplayRef
    );

    if (!quickViewDisplay) {
        return null;
    }
    const title = quickViewDisplay?.title;
    const paragraph = quickViewDisplay?.paragraphs?.[0];
    const { text } = paragraph || {};
    const paragraphAttributeNames = quickViewDisplay?.paragraphAttributeNames;
    const shopAllMsg = (
        <FormattedMessage
            id="abf.SbSharedCreatorDesignsQuickView.viewDetails"
            defaultMessage="Shop All"
        />
    );

    return (
        <div className={classNames(styles.container, { [styles.hidden]: !isVisible })}>
            <Link
                ariaLabel="close quick view"
                dataTn="quick-view-close"
                onClick={onClose}
                className={styles.closeButtonDiv}
            >
                <Close className={styles.close} />
            </Link>
            <div className={styles.scrollArea}>
                <div className={styles.topDiv}>
                    <div className={styles.label}>{title}</div>
                    <span>
                        <QuickViewCreators quickViewDisplay={quickViewDisplay} />
                    </span>
                </div>
                <div className={styles.attrDiv}>
                    {paragraphAttributeNames?.material && (
                        <div className={styles.attr}>
                            <div className={styles.label}>
                                <FormattedMessage
                                    id="abf.SbSharedCreatorDesignsQuickView.Materials"
                                    defaultMessage="Materials"
                                />
                            </div>
                            <div className={styles.arrtibuteList}>
                                {paragraphAttributeNames?.material}
                            </div>
                        </div>
                    )}
                    {paragraphAttributeNames?.style && (
                        <div className={styles.attr}>
                            <div className={styles.label}>
                                <FormattedMessage
                                    id="abf.SbSharedCreatorDesignsQuickView.Styles"
                                    defaultMessage="Styles"
                                />
                            </div>
                            <div className={styles.arrtibuteList}>
                                {paragraphAttributeNames?.style}
                            </div>
                        </div>
                    )}
                </div>
                <div className={styles.paragraph}>{text}</div>
            </div>
            {isMobile ? (
                <SeoLink linkData={quickViewDisplay?.linkData} dataTn="quick-view-item-button">
                    {shopAllMsg}
                </SeoLink>
            ) : (
                <SeoButton
                    className={styles.shopAllButton}
                    type="secondary"
                    size="small"
                    fullWidth
                    linkData={quickViewDisplay?.linkData}
                    dataTn="quick-view-item-button"
                >
                    {shopAllMsg}
                </SeoButton>
            )}
        </div>
    );
};
